/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { useInventoryState } from "../../hooks/useInventoryState";
import { ToggleButtonGroup, ToggleButton, Tooltip, IconButton } from "@mui/material";
import { useHookstateEffect } from '@hookstate/core';
import { motion } from "framer-motion"
import { useThemeState } from "../../hooks/useThemeState";
import { Menu, ViewList, ViewModule } from "@mui/icons-material";
import { useProductBrowserState } from "../../hooks/useProductBrowserState";
import { BreadCrumbs } from "../../components/ProductBrowser/BreadCrumbs";
import React, { useRef } from "react";
import { useCheckoutFormState } from "../../hooks/useCheckoutFormState";
import { Sidebar } from "./Sidebar/Sidebar";
import { ProductSearchBox } from "./ProductSearchBox";
import { ItemWidthSlider } from "./ItemWidthSlider";
import { ProductBrowserGrid } from "./GridView/ProductBrowserGrid";
import { ProductBrowserList } from "./ListView/ProductBrowserList";
import { useTranslate } from "../../hooks/useTranslate/useTranslate";
import { CustomScrollbars } from "../CustomScrollbars/CustomScrollbars";
import { useSessionState } from "../../hooks/useSessionState";
import { useStorefrontSettingsState } from "../../hooks/useStorefrontSettingsState";
import { LandingPage } from "./LandingPage";
import { useCatalogState } from "../../hooks/useCatalogState";
import { CatalogMenu } from "../CatalogMenu";
import { LoadingDots } from "../LoadingDots";
import { useTailwindMediaQueries } from "../../hooks/useTailwindMediaQueries";
import { SelectLocation } from "../CheckoutForm/SelectLocation";
import { CheckoutDate } from "../CheckoutForm/CheckoutDate";
import { FwQueryState } from "../../hooks/useFwQuery";
import { Product } from "../../hooks/useInventoryState";
import { useScrollPosition } from "../../hooks/useScrollPosition";

export type ProductBrowserProps = {
    catalogid: string | undefined;
    query?: FwQueryState<Product>;
}
export const ProductBrowser: React.FC<ProductBrowserProps> = props => {
    const inventoryState = useInventoryState();
    // Use the query prop if provided, otherwise fall back to the old system
    const products = props.query ? {
        Items: props.query.items,
        PageNo: props.query.pageNo,
        PageSize: props.query.pageSize,
        TotalItems: props.query.totalItems,
        Sort: props.query.sort
    } : inventoryState.getProductsState().get().data;
    const productBrowserState = useProductBrowserState();
    const view = productBrowserState.get().view;
    //const isDesktop = useIsDesktopMediaQuery();
    const sidebarExpanded = inventoryState.get().sidebarExpanded;
    const themeState = useThemeState();
    const theme = themeState.get().theme;
    const themeName = themeState.get().themeName;
    const isDarkTheme = themeName === 'dark';
    const translate = useTranslate();
    const checkoutFormState = useCheckoutFormState();
    let durationDays = checkoutFormState.getDurationInDays();
    const showLandingPage = inventoryState.get().showLandingPage;
    const catalogState = useCatalogState();

    const warehouse = checkoutFormState.getWarehouse();
    const warehouseId = warehouse?.WarehouseId;

    const coLocation = checkoutFormState.getLocation();
    const locationId = coLocation?.LocationId;
    const invLocation = inventoryState.get().location;
    let locationChanged = false;
    if (invLocation === undefined) {
        locationChanged = true;
    }
    else if (coLocation !== undefined) {
        if (coLocation.LocationId !== invLocation.LocationId) {
            locationChanged = true;
        }
    }
    const sessionState = useSessionState();
    const loggedIn = sessionState.get().loggedIn;
    const storefrontSettingsState = useStorefrontSettingsState();
    const settings = storefrontSettingsState.get().settings;
    const mediaQueries = useTailwindMediaQueries();

    const separateQuoteRequestsByCatalog = settings?.SeparateQuoteRequestsByCatalog ?? false;

    // Add scroll position management
    const mainContainerRef = useRef<HTMLDivElement>(null);
    const { isWaitingForScrollRestoration } = useScrollPosition(mainContainerRef);

    // Smart loading logic - show loading dots when data is being fetched
    const shouldShowLoading = () => {
        const isLoading = props.query ? props.query.isPending : inventoryState.getProductsState().get().isLoading;
        
        // Always show loading when data is being fetched, regardless of browser
        return isLoading;
    };

    useHookstateEffect(() => {
        if (settings && ((settings.IsAnonymousAccessEnabled) || (!settings.IsAnonymousAccessEnabled && loggedIn))) {
            inventoryState.setProductFilterAsync({
                catalog: inventoryState.get().catalog,
                warehouse: warehouse,
                location: locationChanged ? coLocation : undefined,
                showLandingPage: showLandingPage
            });
        }
    }, [loggedIn, settings, warehouseId, locationId, showLandingPage])

    return <>
        <div css={css`
                flex: 1 1 0;
                display:flex;
                flex-direction:column;
                overflow: hidden;
                position: relative;`}>
            {shouldShowLoading() &&
                <div css={css`
                        position:fixed;
                        top:0;
                        right:0;
                        bottom:0;
                        left:0;
                        /*background-color:#FFFFFF55;*/
                        z-index:4;
                        display:flex;
                        align-items:center;
                        justify-content:center;`}>
                    <LoadingDots />
                </div>
            }
            {/* Show loading dots while waiting for scroll restoration to prevent jumping */}
            {isWaitingForScrollRestoration &&
                <div css={css`
                        position:fixed;
                        top:0;
                        right:0;
                        bottom:0;
                        left:0;
                        /*background-color:${isDarkTheme ? '#1a1a1a' : '#ffffff'};*/
                        z-index:3;
                        display:flex;
                        align-items:center;
                        justify-content:center;`}>
                    <LoadingDots />
                </div>
            }
            {/* Hide main content while waiting for scroll restoration */}
            <div 
                ref={mainContainerRef}
                css={css`
                        flex: 1 1 0;
                        display: flex;
                        flex-direction: column;
                        overflow:hidden;
                        /*${isWaitingForScrollRestoration ? 'visibility: hidden;' : ''}*/`}>
                <div css={css`flex: 1 1 0;display:flex;`}>
                    {(props.catalogid && mediaQueries.minw_md) && (
                        <motion.div
                            css={css`
                                    flex:0 0 auto;
                                    ${sidebarExpanded ? 'width: 300px;' : ''}
                                    display:flex;
                                    flex-direction: column;
                                    transition:background-color 1s;
                                    font-size: .8em;
                                    overflow:hidden;
                                `}
                            style={{
                                backgroundColor: theme.palette.siteSideBar
                            }}
                            transition={{ type: "spring", stiffness: 200, damping: 22 }}>
                            <Sidebar />
                        </motion.div>
                    )}
                    <div
                        css={css`
                                flex:1 1 0;
                                display:flex;
                                flex-wrap: wrap;
                                flex-direction: column;
                            `}
                    >
                        {/* less than 768 (phone) */}
                        {mediaQueries.maxw_sm && (<div css={css`display:flex;align-items:center;flex-wrap:wrap;`}>
                            <div css={css`flex: 1 1 auto;display:flex;flex:0 0 auto;justify-content:flex-end;`}>
                                <div css={css`flex:0 0 auto;`}>
                                    <Tooltip title={translate('Search Options')}>
                                        <IconButton 
                                            aria-label={translate('Search Options')} 
                                            size="large" 
                                            onClick={async e => {
                                                return Promise.resolve().then(() => {
                                                    inventoryState.setSidebarExpanded(true);
                                                    })
                                                }
                                            }
                                        >
                                            <Menu fontSize="large" />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </div>
                            <div css={css`flex: 1 1 0;font-size:.8em;white-space:nowrap;cursor:pointer;`}>
                                <div>
                                    <div><span css={css`color:${themeState.isDark() ? '#ccc' : '#777'};`}>{translate('Location')}: </span>{checkoutFormState.getLocation()?.Location ?? ''}</div>
                                    {(inventoryState.get().catalog?.HasRental ?? false) && (
                                        <div><span css={css`color:${themeState.isDark() ? '#ccc' : '#777'};`}>{translate('From')}: </span>{new Date(checkoutFormState.get().fromDateValue).toLocaleDateString()} - {new Date(checkoutFormState.get().toDateValue).toLocaleDateString()}</div>
                                    )}
                                    <div css={css`display:flex;align-items:center;padding: 0 .5rem 0 0;`}>
                                        <CatalogMenu />
                                    </div>
                                </div>
                            </div>
                            <div css={css`flex:1 1 0;display: flex;padding:.5rem .5rem`}>
                                <ProductSearchBox />
                            </div>
                        </div>)}

                        {/* less than 1280 (tablet) */}
                        {(mediaQueries.tablet) &&  (
                            <div css={css`padding:.4rem 0 0 .4rem;display:flex;`}>
                                {/* <div css={css`flex: 1 1 auto;display:flex;flex:0 0 auto;align-items:center;
                                    justify-content:flex-end;`}>
                                    <div css={css`flex:0 0 auto;`}
                                        onClick={async e => {
                                            return Promise.resolve().then(() => {
                                                inventoryState.setSidebarExpanded(true);
                                            })
                                        }}>
                                        <Tooltip title={translate('Search Options')}>
                                            <IconButton avria-label={translate('Search Options')} size="large">
                                                <Menu css={css`font-size:32px;`} fontSize="large" />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                </div> */}
                                <div css={css`flex:1 1 0;display:flex;align-items:center;flex-wrap:wrap;`}>
                                    {!separateQuoteRequestsByCatalog && !inventoryState.get().sidebarExpanded && (
                                        <div css={css`padding:0 5px 0 .5rem;flex:1 1 0;`}>
                                            <SelectLocation disabled={false} css={css`flex:1 1 0;`} />
                                        </div>
                                    )}
                                    <div css={css`flex: 0 0 auto;padding:0 0 0 .5rem;`}>
                                        <CheckoutDate type="startdate" catalogId={inventoryState.get().catalog?.Id} />
                                    </div>    
                                    <span css={css`flex: 0 0 auto;padding:0 .5rem;`}>-</span>
                                    <div>
                                        <CheckoutDate type="stopdate" catalogId={inventoryState.get().catalog?.Id} />
                                    </div>
                                    <span css={css`flex: 0 0 auto;padding:0 .5rem;`}>({durationDays} {translate('days')})</span>
                                    <div css={css`flex:1 1 0;display: flex;padding:.5rem .5rem`}>
                                        <ProductSearchBox />
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {(props.catalogid) && (<>
                            <div css={css`
                                flex: 0 0 auto;
                                padding: 2px 0;
                                overflow: hidden;
                                display: flex;
                                flex-wrap: wrap;`}>
                                {mediaQueries.minw_xl && (
                                    <div css={css`flex:1 1 0;display: flex;padding:.5rem .8rem;`}>
                                        <ProductSearchBox />
                                    </div>
                                )}
                                <div css={css`
                                    flex: 0 0 auto;
                                    align-self: flex-end;
                                    padding:.25rem .75rem;
                                    display:flex;
                                    align-items:center;
                                    @media (min-width: 720px) {
                                        flex: 0 0 auto;
                                    }
                                    `}>
                                    {mediaQueries.minw_xl && (<>
                                        <ItemWidthSlider />
                                        <ToggleButtonGroup
                                            orientation="horizontal"
                                            value={productBrowserState.get().view}
                                            exclusive
                                            onChange={async (e, value) => {
                                                return Promise.resolve().then(() => {
                                                    if (value) {
                                                        productBrowserState.setView(value);
                                                    }
                                                });
                                            }}>
                                            <ToggleButton value="list" aria-label="list">
                                                <Tooltip title={translate('List View')} >
                                                    <ViewList />
                                                </Tooltip>
                                            </ToggleButton>
                                            <ToggleButton value="grid" aria-label="grid">
                                                <Tooltip title={translate('Grid View')} >
                                                    <ViewModule />
                                                </Tooltip>
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </>)}
                                    {/* {(productBrowserState.get().view === 'grid' || !mediaQueries.minw_xl) && (
                                        <ToggleButtonGroup
                                            css={css`padding:0 .25rem;`}
                                            orientation="horizontal"
                                            value={productBrowserState.get().view}
                                            exclusive
                                            onChange={async (e, value) => {
                                                return Promise.resolve().then(() => {
                                                    if (value) {
                                                        productBrowserState.setExpandedView(value);
                                                    }
                                                });
                                            }}>
                                            {productBrowserState.get().expandedView && (
                                                <ToggleButton value={false}>
                                                    <Tooltip title={translate('Collapse')}>
                                                        <UnfoldLess onClick={e => productBrowserState.setExpandedView(false)} />
                                                    </Tooltip>
                                                </ToggleButton>
                                            )}
                                            {!productBrowserState.get().expandedView && (
                                                <ToggleButton value={true}>
                                                    <Tooltip title={translate('Expand')} >
                                                        <UnfoldMore onClick={e => productBrowserState.setExpandedView(true)} />
                                                    </Tooltip>
                                                </ToggleButton>
                                            )}
                                        </ToggleButtonGroup>
                                    )} */}
                                </div>
                            </div>
                        </>)}


                        {(props.catalogid && mediaQueries.phone && sidebarExpanded) && (
                            <motion.div
                                css={css`
                                    position:fixed;
                                    top:0;
                                    right: ${mediaQueries.phone ? '0' : 'unset'};
                                    bottom:0;
                                    left:0;
                                    z-index:4;
                                    flex:0 0 auto;
                                    display:flex;
                                    flex-direction: column;
                                    transition:background-color 1s;
                                    font-size: .9em;
                                    overflow:hidden;
                                    box-shadow: 0 5px 5px rgba(182, 182, 182, 0.75);
                                `}
                                style={{
                                    backgroundColor: isDarkTheme ? '#333333EE' : '#ffffffEE'
                                }}
                                transition={{ type: "spring", stiffness: 200, damping: 22 }}
                            >
                                <Sidebar />
                            </motion.div>
                        )}

                        <div css={css`flex:0 0 auto;padding:.25em 0;display:flex;align-items:center;justify-content:center;`}>
                            <BreadCrumbs></BreadCrumbs>
                        </div>

                        {showLandingPage && (
                            <div css={css`flex:1 1 0;overflow:auto;`}>
                                <div css={css`margin: 0 auto;`}>
                                    {catalogState.get().catalogs && catalogState.get().catalogs?.map((catalog, index, catalogs) => {
                                        if (!props.catalogid || props.catalogid === catalog.Id) {
                                            return (
                                                <LandingPage key={index} catalog={catalog} showCatalogName={props.catalogid !== catalog.Id} showCategoryTreeGrid={true} />
                                            )
                                        }
                                        return null;
                                    })}
                                </div>
                            </div>
                        )}

                        {!showLandingPage && products && (
                            <div
                                css={css`
                                        flex: 1 1 0;
                                        display: flex;
                                        flex-direction: column;
                                        position: relative;
                                        padding: 0 0px 0 0;
                                    `}
                            >
                                <CustomScrollbars 
                                    overridecss={`flex:1 1 0;display:flex;flex-direction:column;`}
                                    data-testid="product-browser-scroll"
                                >
                                    {products.Items.length === 0 && !shouldShowLoading() ? (
                                        <div css={css`
                                            display: flex;
                                            flex-direction: column;
                                            align-items: center;
                                            justify-content: center;
                                            padding: 2rem;
                                            text-align: center;
                                            color: ${themeState.isDark() ? '#ccc' : '#666'};
                                        `}>
                                            <div css={css`font-size: 1.2rem; margin-bottom: 0.5rem;`}>
                                                {translate('No products found')}
                                            </div>
                                            <div css={css`font-size: 0.9rem;`}>
                                                {translate("Try adjusting your search criteria or browse different categories")}
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {((!mediaQueries.minw_xl) || (view === 'grid')) && (
                                                <ProductBrowserGrid products={products} />
                                            )}
                                            {((mediaQueries.minw_xl) && (view === 'list')) && (
                                                <ProductBrowserList products={products} />
                                            )}
                                        </>
                                    )}
                                </CustomScrollbars>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </>
}
